import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import I18NextHttpBackend from 'i18next-http-backend';
import i18next from 'i18next';
import type { LanguageDefinition } from '@jwp/ott-common/types/i18n';
import { filterSupportedLanguages } from '@jwp/ott-common/src/utils/i18n';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { useAppInfo } from '@jwp/ott-hooks-react/src/useAppInfo';

import { NAMESPACES } from './resources';

export const DEFINED_LANGUAGES: LanguageDefinition[] = [
  {
    code: 'es',
    displayName: 'Spanish',
  },
  {
    code: 'en',
    displayName: 'English',
  },
];

const initI18n = async () => {
  const { byDefaultLanguage = 'en' } = useAppConfig();
  const { availableLanguages } = useAppInfo();
  const enabledLanguages = availableLanguages && availableLanguages.length > 0 ? availableLanguages : [byDefaultLanguage];
  const defaultLanguage = `${byDefaultLanguage}`;

  const supportedLanguages = filterSupportedLanguages(DEFINED_LANGUAGES, enabledLanguages);

  useConfigStore.setState({ supportedLanguages });
  if (!supportedLanguages.some(({ code }) => code === defaultLanguage)) {
    throw new Error(`The default language is not enabled: ${defaultLanguage}`);
  }

  const languageCodes = supportedLanguages.map(({ code }) => code);

  await i18next
    .use(I18NextHttpBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      supportedLngs: languageCodes,
      fallbackLng: defaultLanguage,
      returnEmptyString: false,
      ns: NAMESPACES,
      defaultNS: 'common',
      fallbackNS: 'common',
      preload: languageCodes,
      backend: {
        loadPath: '/locales/{{lng}}/{{ns}}.json',
        // Bust browser and service-worker caches when a new app version is deployed.
        queryStringParams: { v: import.meta.env.APP_VERSION ?? 'dev' },
        requestOptions: {
          cache: 'no-store',
        },
      },
      interpolation: {
        escapeValue: false,
      },
      detection: {
        order: ['localStorage', 'navigator'],
        caches: ['localStorage'],
        lookupLocalStorage: 'jwapp.language',
      },
      react: {
        useSuspense: false,
      },
      lng: localStorage.getItem('jwapp.language') ?? defaultLanguage,
    });

  const detectedLanguage = i18next.language;
  if (!supportedLanguages.some(({ code }) => code === detectedLanguage)) { await i18next.changeLanguage(defaultLanguage); }
};

export default initI18n;
